import { useEffect, useState, useContext } from 'react';
import React from 'react';
import { format } from 'date-fns';
import { AuthContext } from '../AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import StaffNav from './managernav';

export default function EditAppraisal() {
  const [appraisals, setAppraisals] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editedValue, setEditedValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [meetingNotes, setMeetingNotes] = useState([]);
  const [meetingNotesModal, setMeetingNotesModal] = useState(false);
  const [previousValue, setPreviousValue] = useState('');
  const [newValue, setNewValue] = useState('');
  const [selectedField, setSelectedField] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const location = useLocation();
  const navigate = useNavigate();
  const id = location.state?.id;
  
  console.log('Appraisal ID:', id);

  useEffect(() => {
    const fetchAppraisals = async () => {
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/viewappraisal/${id}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error("Failed to fetch appraisals data.");
        const data = await response.json();
        setAppraisals(data.data);
        if (data.data && data.data.length > 0) setActiveIndex(0);
      } catch (error) {
        console.error('Error fetching appraisals:', error);
      }
    };
    fetchAppraisals();
  }, [id]);

  useEffect(() => {
    const fetchMeetingNotes = async () => {
      if (appraisals.length === 0 || !appraisals[activeIndex]) return;
      const appraisalId = appraisals[activeIndex].id;
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/meetingnotes/${appraisalId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error("Failed to fetch meeting notes.");
        const data = await response.json();
        setMeetingNotes(data.data);
      } catch (error) {
        console.error('Error fetching meeting notes:', error);
      }
    };
    fetchMeetingNotes();
  }, [activeIndex, appraisals]);

  const handleEditClick = (field, currentValue) => {
    setEditingField(field);
    setEditedValue(currentValue);
  };

  const handleEditSave = async (field) => {
    const appraisalId = appraisals[activeIndex].id;
    const updatedAppraisal = { Field: field, Value: editedValue };
    await fetch(`https://api.insightos.co.uk/api/updateappraisal/${appraisalId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedAppraisal),
    });
    setAppraisals((prev) =>
      prev.map((appraisal, index) =>
        index === activeIndex ? { ...appraisal, [field]: editedValue } : appraisal
      )
    );
    setEditingField(null);
    setEditedValue('');
  };

  const handleDelete = async () => {
    const appraisalId = appraisals[activeIndex].id;
    await fetch(`https://api.insightos.co.uk/api/deleteappraisal/${appraisalId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
    setAppraisals((prev) => prev.filter((_, index) => index !== activeIndex));
    setActiveIndex(0);
  };

  const handleSend = async () => {
    const appraisalId = appraisals[activeIndex].id;
    const StaffId = appraisals[activeIndex].StaffId;
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/employeecommentssend/${appraisalId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ StaffId }),
      });
      if (!response.ok) throw new Error('Failed to send appraisal. Please try again.');
      await response.json();
      setMessage('Appraisal sent for employee comments!');
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'An error occurred. Please try again.');
      setMessageType('error');
    }
  };

  // AI request to backend
  const handleAI = async (fieldName, fieldToAnalyze) => {
    setLoading(true);
    setAiAnalysis(null);
    setSelectedField('');
    try {
      const appraisalId = appraisals[activeIndex].id;
      const response = await fetch('https://api.insightos.co.uk/api/aiappraisal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          field: fieldName,
          text: fieldToAnalyze,
          appraisalId,
          type: 'appraisal',
        }),
      });
      if (!response.ok) throw new Error('AI analysis failed.');
      const data = await response.json();
      const aiText = data?.rewritten || data?.result || '';
      if (aiText) {
        setPreviousValue(fieldToAnalyze);
        setNewValue(aiText);
        setAiAnalysis(aiText);
        setSelectedField(fieldName);
      } else {
        throw new Error('No AI result returned.');
      }
    } catch (error) {
      setMessage(error.message || 'AI analysis error.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToSection = async () => {
    if (!selectedField) {
      alert('Please select a field.');
      return;
    }
    const appraisalId = appraisals[activeIndex].id;
    const updatedAppraisal = { Field: selectedField, Value: newValue };
    await fetch(`https://api.insightos.co.uk/api/updateappraisal/${appraisalId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedAppraisal),
    });
    setAppraisals((prev) =>
      prev.map((appraisal, index) =>
        index === activeIndex
          ? { ...appraisal, [selectedField]: newValue }
          : appraisal
      )
    );
    setAiAnalysis(null);
    setSelectedField('');
    alert('AI analysis applied successfully to the selected field!');
  };

  const currentAppraisal = appraisals[activeIndex] || {};

  const handleCompleted = async () => {
    const appraisalId = appraisals[activeIndex].id;
    const staffId = appraisals[activeIndex].StaffId;
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/appraisalcompleted/${appraisalId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ StaffId: staffId }),
      });
      if (!response.ok) throw new Error('Failed to complete appraisal. Please try again.');
      await response.json();
      setMessage('Appraisal marked as completed!');
      setMessageType('success');
      navigate('/dashboard');
    } catch (error) {
      setMessage(error.message || 'An error occurred. Please try again.');
      setMessageType('error');
    }
  };

  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const formattedText = text
      .split('next')
      .map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && <p className="mt-2" />}
          {part.split(regex).map((subpart, subIndex) =>
            subpart.toLowerCase() === searchTerm.toLowerCase() ? (
              <span key={subIndex} className="bg-yellow-300">{subpart}</span>
            ) : (
              subpart
            )
          )}
        </React.Fragment>
      ));
    return formattedText;
  };

  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  return (
    <div>

      <StaffNav />
      <div className="flex">
        <div className={`w-${aiAnalysis ? '1/2' : 'full'} p-4`}>
          <div className="px-4 sm:px-0">
            <h3 className="text-base font-semibold text-gray-900">Appraisal Information</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Details about the employee's performance and feedback.</p>
          </div>
          <div className="mt-6 border-t border-gray-100">
            <div className="flex space-x-4 mb-4">
              {appraisals
                .sort((a, b) => new Date(a.ReviewPeriodFrom) - new Date(b.ReviewPeriodFrom))
                .map((appraisal, index) => (
                  <button
                    key={index}
                    className={`px-4 py-2 text-sm font-medium ${index === activeIndex ? 'bg-purple-600 text-white' : 'bg-purple-100 text-gray-500'}`}
                    onClick={() => setActiveIndex(index)}
                  >
                    {format(new Date(appraisal?.ReviewPeriodFrom), 'MMM dd, yyyy')}
                  </button>
                ))}
            </div>
            <div className="flex space-x-4 mb-4">
              <button onClick={() => navigate('/dashboard')} className="rounded-md bg-orange-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
                Go Back
              </button>
              <button
                className="rounded-md bg-red-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                onClick={handleDelete}
              >
                Delete Appraisal
              </button>
              <button
                className="rounded-md bg-green-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                onClick={handleSend}
                data-component="edit-appraisal-actions"
                data-time-saved="5"
                data-feature="send_for_employee_comments_put"
              >
                Send For Employee Comments
              </button>
              <button
                onClick={handleCompleted}
                className='rounded-md bg-blue-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500'
                data-component="edit-appraisal-actions"
                data-time-saved="10"
                data-feature="mark_appraisal_completed_put"
              >
                Appraisal Completed
              </button>
              {meetingNotes && (
                <button onClick={() => setMeetingNotesModal(true)} className='rounded-md bg-purple-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500'>
                  Meeting Notes
                </button>
              )}
            </div>
            {message && (
              <div
                className={`p-4 rounded-md text-white ${
                  messageType === 'error' ? 'bg-red-600' : 'bg-green-600'
                }`}
              >
                {message}
              </div>
            )}
            <dl className="divide-y divide-gray-100">
              {currentAppraisal && Object.keys(currentAppraisal).length > 0 ? (
                Object.keys(currentAppraisal).map((field) => {
                  const excludedFields = ['CreatedAt', 'UpdatedAt', 'id', 'StaffId'];
                  const dateFields = ['CreatedAt', 'UpdatedAt', 'ReviewPeriodFrom', 'ReviewPeriodTo'];
                  if (excludedFields.includes(field)) return null;
                  return (
                    <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0" key={field}>
                      <dt className="text-sm font-medium text-gray-900">
                        {field.replace(/([A-Z])/g, ' $1')}
                      </dt>
                      <dd className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                        <div>
                          {editingField === field ? (
                            <>
                              <textarea
                                value={editedValue}
                                onChange={(e) => setEditedValue(e.target.value)}
                                className="border p-2 rounded w-full h-32 resize-y"
                              />
                              <button
                                onClick={() => handleEditSave(field)}
                                className="mt-2 rounded-md bg-green-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                                data-component="edit-appraisal-fields"
                                data-time-saved="5"
                                data-feature="save_field_put"
                              >
                                Save
                              </button>
                            </>
                          ) : (
                            <>
                              {dateFields.includes(field) && currentAppraisal[field] ? (
                                format(new Date(currentAppraisal[field]), 'MMM dd, yyyy')
                              ) : (
                                currentAppraisal[field] || 'N/A'
                              )}
                              <div className="mt-2">
                                <button
                                  onClick={() => handleEditClick(field, currentAppraisal[field])}
                                  className="ml-2 text-blue-600"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleAI(field, currentAppraisal[field])}
                                  className="ml-10 rounded-md bg-green-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                                  disabled={loading}
                                  data-component="edit-appraisal-ai"
                                  data-time-saved="10"
                                  data-feature="request_ai_analysis_post"
                                >
                                  {loading && selectedField === field ? 'Analyzing...' : 'AI Analyze'}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </dd>
                    </div>
                  );
                })
              ) : (
                <div className="p-4 text-center text-gray-500">No appraisal data available.</div>
              )}
            </dl>
          </div>
        </div>
        {meetingNotesModal && (
          <div className="w-1/2 border-l p-4 top-16 max-h-screen overflow-y-auto relative">
            <button
              onClick={() => setMeetingNotesModal(false)}
              className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm text-white shadow-sm hover:bg-indigo-500"
            >
              Close
            </button>
            <h3 className="text-lg font-semibold">Meeting Notes</h3>
            <p className="mt-1 text-sm text-gray-500">Notes taken during the appraisal meeting.</p>
            <div className="mt-4">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search meeting notes..."
                className="border p-2 rounded w-full mb-4"
              />
            </div>
            <div className="mt-4 mb-4">
              {meetingNotes.map((note, index) => (
                <div key={index} className="bg-purple-100 p-2 rounded mt-2">
                  <p className="text-sm font-semibold">
                    {highlightText(note.MeetingNotes, searchTerm)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {format(new Date(note.Date), "MMM dd, yyyy")}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        {aiAnalysis && (
          <div className="w-1/2 border-l p-4 sticky top-16 max-h-screen overflow-y-auto">
            <h3 className="text-lg font-semibold">AI Analysis</h3>
            <p className="mt-1 text-sm text-gray-500">Analyze the text and apply the rewrite to the section.</p>
            <p className="mt-4 text-sm font-semibold">Selected Field: {selectedField}</p>
            <textarea
              value={aiAnalysis}
              onChange={(e) => setAiAnalysis(e.target.value)}
              className="w-full h-96 p-2 border rounded mt-2"
            />
            <div className="mt-4">
              <button
                onClick={handleAddToSection}
                className="ml-4 rounded-md bg-indigo-600 px-4 py-2 text-sm text-white shadow-sm hover:bg-indigo-500"
                data-component="edit-appraisal-ai"
                data-time-saved="2"
                data-feature="apply_ai_rewrite_put"
              >
                Apply Rewrite to Section
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
